import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api from "@/lib/api";
import { trackOutboundClick } from "@/hooks/useAnalytics";
import { SeoHead } from "@/components/public/SeoHead";
import { JsonLd } from "@/components/public/JsonLd";
import { SustainabilityScoreBadge } from "@/components/public/SustainabilityScoreBadge";
import { ClassificationBadge } from "@/components/public/ClassificationBadge";
import { ClothingItemCard } from "@/components/public/ClothingItemCard";
import { AdSlot } from "@/components/public/AdSlot";
import { ModalScroll } from "@/components/public/ModalScroll";
import { ChevronLeftIcon, ChevronRightIcon, ExternalLink, ChevronLeft, Quote, AlertOctagon } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function BrandDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["brand-detail", slug],
    queryFn: async () => (await api.get(`/public/brands/${slug}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-muted_text">Loading…</div>;
  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-14 text-center">
        <h1 className="editorial-headline text-5xl mb-4">Brand not found</h1>
        <Link to="/brands" className="text-forest underline">Back to brands</Link>
      </div>
    );
  }
  const b = data.brand;
  const warningsTotal =
    (b.warnings?.length || 0) +
    (data.materials_disputed_count || 0) +
    (data.materials_mislabeled_count || 0);

  // JSON-LD structured data
  const origin = typeof window !== "undefined" ? window.location.origin : "https://sustainablycurated.com";
  const brandUrl = `${origin}/brands/${slug}`;
  const sameAs = [b.website_url, ...(b.social_links || []).map((s) => (typeof s === "string" ? s : s.url))].filter(Boolean);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: b.name,
      description: b.description_short,
      url: b.website_url || brandUrl,
      logo: b.logo_url || undefined,
      sameAs: sameAs.length ? sameAs : undefined,
      address: b.country ? { "@type": "PostalAddress", addressCountry: b.country } : undefined,
      aggregateRating: b.sustainability_score != null && b.total_reviewed_items
        ? {
            "@type": "AggregateRating",
            ratingValue: Math.round(b.sustainability_score),
            bestRating: 100,
            worstRating: 0,
            ratingCount: b.total_reviewed_items,
          }
        : undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Brands", item: origin + "/brands" },
        { "@type": "ListItem", position: 3, name: b.name, item: brandUrl },
      ],
    },
  ];

  // Determine the first available menu section to set as default active
  // "Our take" section is only visible on the brand page when the editorial text exists.
  // The images alone are still shown on linked clothing items pages (handled separately by the backend).
  const availableSections = [
    b.our_take ? "brand-our-take-section" : null,
    "brand-score-section",
    (data.top_materials?.length || 0) > 0 ? "brand-materials-section" : null,
    (data.certifications?.length || 0) > 0 ? "brand-certifications-section" : null,
    (b.sustainability_claims?.length || 0) > 0 ? "brand-claims-section" : null,
    (b.warnings?.length || 0) > 0 ? "brand-warnings-section" : null,
  ].filter(Boolean);
  const effectiveActive = activeSection || availableSections[0] || "brand-score-section";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="brand-detail-page">
      <SeoHead
        title={b.seo_title || `${b.name} — Sustainability Score ${b.sustainability_score != null ? Math.round(b.sustainability_score) + "%" : "not yet scored"}`}
        description={b.seo_description || b.description_short || `Sustainability review of ${b.name}. ${b.total_approved_items || 0} approved items out of ${b.total_reviewed_items || 0} reviewed.`}
        path={`/brands/${slug}`}
        image={b.logo_url}
      />
      <JsonLd data={jsonLd} />

      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted_text hover:text-forest mb-6" data-testid="brand-back-button">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <section className="grid md:grid-cols-[260px_1fr] gap-8 mb-8 md:items-start" data-testid="brand-from-website-section">
        <div className="relative md:h-[260px]">
          {b.logo_url ? (
            <img
              src={b.logo_url}
              alt={`${b.name} logo`}
              className="w-full h-full md:h-full aspect-square md:aspect-auto object-contain bg-white p-6 rounded-2xl border border-line"
            />
          ) : (
            <div className="w-full aspect-square md:aspect-auto md:h-full rounded-2xl border border-line bg-white" aria-hidden />
          )}
          {b.is_featured && (
            <span
              className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-1 bg-leaf/15 text-leaf"
              data-testid="brand-detail-featured-pill"
            >
              Featured
            </span>
          )}
        </div>

        <div className="flex flex-col md:h-[260px] min-h-0">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4 shrink-0">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-muted_text mb-2">{[b.country, b.region].filter(Boolean).join(" · ") || "Brand"}</div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="editorial-headline text-2xl sm:text-3xl leading-none">{b.name}</h1>
                {b.website_url && (
                  <a
                    href={b.website_url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center justify-center gap-1.5 text-sm bg-forest text-ivory rounded-full px-5 py-2.5 hover:bg-forest/90 whitespace-nowrap min-w-[150px]"
                    data-testid="brand-website-link"
                    onClick={() => trackOutboundClick({ target_type: "brand", target_slug: b.slug, destination_url: b.website_url })}
                    onAuxClick={() => trackOutboundClick({ target_type: "brand", target_slug: b.slug, destination_url: b.website_url })}
                  >
                    Visit website <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {warningsTotal > 0 && (
                  <span
                    className="inline-flex items-center justify-center gap-1.5 text-sm bg-clay text-ivory rounded-full px-5 py-2.5 whitespace-nowrap min-w-[150px]"
                    data-testid="brand-detail-warning-badge"
                  >
                    <AlertOctagon className="w-3.5 h-3.5" />
                    {warningsTotal} warning{warningsTotal === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0" data-testid="brand-score-block">
              <span className="text-xs uppercase tracking-[0.2em] text-muted_text whitespace-nowrap">Sustainably Curated score</span>
              <SustainabilityScoreBadge score={b.sustainability_score} size="lg" />
            </div>
          </div>

          {b.description_short ? (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted_text mb-3 shrink-0">
                <Quote className="w-3.5 h-3.5" />
                <span>From the brand&apos;s website</span>
              </div>
              <ModalScroll className="border-l-2 border-forest/30 pl-4 italic" testId="brand-description-quote">
                <blockquote>
                  <p className="text-base text-ink/90 leading-relaxed whitespace-pre-line">{b.description_short}</p>
                </blockquote>
              </ModalScroll>
              <p className="text-[11px] text-muted_text mt-2 not-italic shrink-0">
              Quoted from the brand.{b.updated_at ? ` Brand information last reviewed on ${formatDate(b.updated_at)}.` : ""} <strong>Editorial review and sustainability scoring are independent.</strong>
            </p>
            </>
          ) : (
            <p className="text-sm text-muted_text italic">No description provided by the brand yet.</p>
          )}
        </div>
      </section>

      {/* Two-column layout: side nav + content */}
      <div className="grid lg:grid-cols-[220px_1fr] gap-8 mb-6">
        <BrandSectionNav data={data} brand={b} active={effectiveActive} onSelect={setActiveSection} />
        <div className="min-w-0">

      {effectiveActive === "brand-our-take-section" && b.our_take && (
        <section id="brand-our-take-section" className="mb-6" data-testid="brand-our-take">
          <div className="text-xs uppercase tracking-[0.2em] text-muted_text mb-4">Our take</div>
          <div className="flex flex-col-reverse sm:flex-row gap-5 items-start">
            <p className="flex-1 text-sm text-muted_text leading-relaxed whitespace-pre-line line-clamp-[10]">
              {b.our_take}
            </p>
            {b.our_take_images && b.our_take_images.length > 0 && (
              <OurTakeImageCarousel
                images={b.our_take_images}
                approvedItems={data.approved_items || []}
              />
            )}
          </div>
        </section>
      )}

      {/* Score breakdown */}
      {effectiveActive === "brand-score-section" && (
      <section id="brand-score-section" className="bg-champagne/30 rounded-3xl p-4 mb-5">
        <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
          <div className="text-xs uppercase tracking-[0.2em] text-muted_text">How the score is calculated</div>
          <Link
            to="/methodology"
            className="text-xs text-forest underline underline-offset-4 hover:text-leaf"
            data-testid="brand-methodology-link"
          >
            How brand scores work →
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-base text-ink/85 mb-2" data-testid="brand-score-formula">
          <span className="font-serif italic">Score</span>
          <span className="text-muted_text">=</span>
          <span><strong>{b.total_approved_items || 0}</strong> approved</span>
          <span className="text-muted_text">÷</span>
          <span><strong>{b.total_reviewed_items || 0}</strong> reviewed</span>
          <span className="text-muted_text">× 100</span>
          <span className="text-muted_text">=</span>
          <SustainabilityScoreBadge score={b.sustainability_score} size="md" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
          <Stat label="Pending" value={data.pending_crawler_results} />
          <Stat label="Reviewed" value={b.total_reviewed_items} />
          <Stat label="Approved" value={b.total_approved_items} variant="green" />
          <Stat label="Rejected" value={b.total_rejected_items} variant="red" />
        </div>
        <div className="mt-2" data-testid="brand-approved-class-breakdown">
          <div className="text-[11px] uppercase tracking-wider text-muted_text mb-1.5">Number of approved items by classification</div>
          <div className="grid grid-cols-3 gap-2">
            <ClassTile classification="GREEN" count={data.approved_class_counts?.GREEN || 0} />
            <ClassTile classification="YELLOW" count={data.approved_class_counts?.YELLOW || 0} />
            <ClassTile classification="RED" count={data.approved_class_counts?.RED || 0} />
          </div>
        </div>
        <p className="text-xs text-muted_text mt-4" data-testid="brand-score-disclaimer">
          Last crawled: {formatDate(b.last_crawled_at)}. Score is based on the clothing items we have manually reviewed. Brand product compositions can change over time.
        </p>
      </section>
      )}

      {effectiveActive === "brand-score-section" && (
        <p className="text-sm text-muted_text mb-6">
          See all the brand's approved clothing items in our{" "}
          <Link
            to={`/clothing?brand_id=${b.id}`}
            className="text-forest underline underline-offset-4 hover:no-underline"
            data-testid="brand-see-all-approved-link"
          >
            clothing list
          </Link>
          .
        </p>
      )}

      {effectiveActive === "brand-materials-section" && data.top_materials?.length > 0 && (
        <section id="brand-materials-section" className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted_text mb-4">Common materials in reviewed items</div>
          <p className="text-sm text-muted_text mb-6">
            See our{" "}
            <Link to="/materials-guide" className="text-forest underline underline-offset-4 hover:no-underline" data-testid="brand-materials-guide-link">
              Materials Guide
            </Link>{" "}
            for an explanation of what each material is and how it's classified.
          </p>
          <div className="flex flex-wrap gap-2">
            {data.top_materials.map((m) => {
              const inner = (
                <>
                  <ClassificationBadge classification={m.classification} className="text-[10px]" />
                  <span>{m.name}</span>
                  <span className="text-muted_text">×{m.count}</span>
                </>
              );
              return m.slug ? (
                <Link
                  key={m.name}
                  to={`/materials/${m.slug}`}
                  className="flex items-center gap-2 px-3 py-1 bg-white border border-line rounded-full text-sm hover:border-forest/40 transition-colors"
                  data-testid={`brand-top-material-${m.slug}`}
                >
                  {inner}
                </Link>
              ) : (
                <div
                  key={m.name}
                  className="flex items-center gap-2 px-3 py-1 bg-white border border-line rounded-full text-sm"
                  data-testid={`brand-top-material-${m.name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {effectiveActive === "brand-certifications-section" && data.certifications?.length > 0 && (
        <section id="brand-certifications-section" className="mb-6" data-testid="brand-certifications-section">
          <div className="text-xs uppercase tracking-[0.2em] text-muted_text mb-4">Certifications</div>
          <p className="text-sm text-muted_text mb-6">
            See our{" "}
            <Link to="/brand-certifications" className="text-forest underline underline-offset-4 hover:no-underline" data-testid="brand-certifications-list-link">
              Brand Certifications
            </Link>{" "}
            list for what each company-level standard means and how it's earned.
          </p>
          <div className="flex flex-wrap gap-2">
            {data.certifications.map((c) => (
              <Link key={c.id} to={`/brand-certifications/${c.slug}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-leaf/10 border border-leaf/30 hover:border-leaf/60 text-sm transition-colors" data-testid={`brand-cert-${c.slug}`}>
                {c.logo_url && (
                  <img src={c.logo_url} alt="" className="h-5 w-5 object-contain" loading="lazy" />
                )}
                <span>{c.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {b.sustainability_claims?.length > 0 && effectiveActive === "brand-claims-section" && (
        <section id="brand-claims-section" className="border border-leaf/30 bg-leaf/5 rounded-3xl p-6 mb-6" data-testid="brand-claims-section">
          <div className="text-xs uppercase tracking-[0.2em] text-muted_text mb-3">Sustainability claims</div>
          <p className="text-xs text-muted_text mb-4">
            Statements this brand makes about its own sustainability practices. Each links to the original source so you can verify them yourself.
          </p>
          <ul className="space-y-3">
            {b.sustainability_claims.map((c, i) => (
              <li key={i} className="bg-white/70 border border-leaf/20 rounded-2xl p-4">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <h3 className="font-medium text-base">{c.title || "Claim"}</h3>
                  {c.live_page ? (
                    <span className="text-xs text-muted_text italic">· Live page</span>
                  ) : c.published_at ? (
                    <span className="text-xs text-muted_text">· {formatDate(c.published_at)}</span>
                  ) : null}
                  {c.url && (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs text-forest hover:underline inline-flex items-center gap-1"
                      data-testid={`claim-source-${i}`}
                    >
                      Source <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {c.description && (
                  <p className="text-sm text-ink/80 mt-1 whitespace-pre-line">{c.description}</p>
                )}
                {c.editor_note && (
                  <div className="mt-3 pt-3 border-t border-leaf/15" data-testid={`claim-editor-note-${i}`}>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-leaf/90 mb-1">Editor's note</div>
                    <p className="text-sm text-ink/75 italic whitespace-pre-line">{c.editor_note}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {effectiveActive === "brand-warnings-section" && (b.warnings?.length > 0 || (data.materials_disputed_count || 0) > 0 || (data.materials_mislabeled_count || 0) > 0) && (
        <section id="brand-warnings-section" className="border border-clay/30 bg-clay/5 rounded-3xl p-6 mb-6" data-testid="brand-warnings-section">
          <div className="text-xs uppercase tracking-[0.2em] text-clay mb-3">Warnings</div>

          {((data.materials_disputed_count || 0) > 0 || (data.materials_mislabeled_count || 0) > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              {(data.materials_mislabeled_count || 0) > 0 && (
                <Link
                  to={`/clothing?brand_id=${b.id}&include_flagged=1`}
                  className="bg-white/70 border border-clay/30 rounded-2xl p-4 hover:border-clay/60 transition-colors"
                  data-testid="warning-mislabeled-count"
                >
                  <div className="text-sm text-ink/85 flex items-baseline gap-2">
                    <strong className="text-clay font-serif text-2xl">{data.materials_mislabeled_count}</strong>
                    <span>item{data.materials_mislabeled_count === 1 ? "" : "s"} with <strong>mislabeled materials</strong> (confirmed by our editors)</span>
                  </div>
                </Link>
              )}
              {(data.materials_disputed_count || 0) > 0 && (
                <Link
                  to={`/clothing?brand_id=${b.id}&include_flagged=1`}
                  className="bg-white/70 border border-amber/30 rounded-2xl p-4 hover:border-amber/60 transition-colors"
                  data-testid="warning-disputed-count"
                >
                  <div className="text-sm text-ink/85 flex items-baseline gap-2">
                    <strong className="text-amber font-serif text-2xl">{data.materials_disputed_count}</strong>
                    <span>item{data.materials_disputed_count === 1 ? "" : "s"} with <strong>disputed materials</strong> (under review)</span>
                  </div>
                </Link>
              )}
            </div>
          )}

          {b.warnings?.length > 0 && (
            <>
              <p className="text-xs text-muted_text mb-3">
                Public references where this brand was found to be non-sustainable in its practices, or made claims that didn&apos;t hold up. Each links to the source so you can read the full story.
              </p>
              <ul className="space-y-3">
                {b.warnings.map((w, i) => (
                  <li key={i} className="bg-white/70 border border-clay/20 rounded-2xl p-4">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <h3 className="font-medium text-base">{w.title || "Warning"}</h3>
                      {w.live_page ? (
                        <span className="text-xs text-muted_text italic">· Live page</span>
                      ) : w.published_at ? (
                        <span className="text-xs text-muted_text">· {formatDate(w.published_at)}</span>
                      ) : null}
                      {w.url && (
                        <a
                          href={w.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-xs text-clay hover:underline inline-flex items-center gap-1"
                          data-testid={`warning-source-${i}`}
                        >
                          Source <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    {w.description && (
                      <p className="text-sm text-ink/80 mt-1 whitespace-pre-line">{w.description}</p>
                    )}
                    {w.editor_note && (
                      <div className="mt-3 pt-3 border-t border-clay/15" data-testid={`warning-editor-note-${i}`}>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-clay/90 mb-1">Editor's note</div>
                        <p className="text-sm text-ink/75 italic whitespace-pre-line">{w.editor_note}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {data.confirmed_false_advertising?.length > 0 && effectiveActive === "brand-warnings-section" && (
        <section className="border border-clay/30 bg-clay/5 rounded-3xl p-6 mb-6" data-testid="brand-false-ad-block">
          <div className="text-xs uppercase tracking-[0.2em] text-clay mb-3">Confirmed false advertising flags</div>
          <ul className="space-y-3 text-sm">
            {data.confirmed_false_advertising.map((r) => (
              <li key={r.id} className="border-l-2 border-clay pl-3">
                <div className="font-medium">{r.claim_description}</div>
                <div className="text-muted_text">{r.actual_material_description}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

        </div>
      </div>

      <AdSlot slotKey="brand_detail" className="w-[111.111vw] ml-[calc(50%-55.555vw)]" />

      {data.approved_items?.length > 0 && (
        <section
          className="w-[111.111vw] ml-[calc(50%-55.555vw)] border-t border-line"
          data-testid="brand-approved-items-separator"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8" data-testid="brand-approved-items">
            <div className="flex items-end justify-between mb-5">
              <h2 className="font-serif text-3xl">Last approved clothing items</h2>
              <Link
                to={`/clothing?brand_id=${b.id}`}
                className="text-sm text-forest underline underline-offset-4 whitespace-nowrap"
                data-testid="brand-approved-see-all"
              >
                See all →
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {data.approved_items.slice(0, 5).map((item) => (
                <ClothingItemCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function BrandSectionNav({ data, brand, active, onSelect }) {
  const sections = [
    { id: "brand-our-take-section", label: "Our take", show: !!brand.our_take },
    { id: "brand-score-section", label: "How the score is calculated", show: true },
    { id: "brand-materials-section", label: "Common materials in reviewed items", show: (data.top_materials?.length || 0) > 0 },
    { id: "brand-certifications-section", label: "Certifications", show: (data.certifications?.length || 0) > 0 },
    { id: "brand-claims-section", label: "Sustainability claims", show: (brand.sustainability_claims?.length || 0) > 0 },
    { id: "brand-warnings-section", label: "Warnings", show: (brand.warnings?.length || 0) > 0 },
  ].filter((s) => s.show);

  return (
    <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="brand-sidenav">
      <p className="text-xs uppercase tracking-[0.2em] text-forest/70 mb-4">On this page</p>
      <ul className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible border-l border-line">
        {sections.map((s) => {
          const isActive = active === s.id;
          return (
            <li key={s.id} className="shrink-0">
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                data-testid={`brand-nav-${s.id}`}
                aria-current={isActive ? "true" : undefined}
                className={`w-full text-left font-serif text-sm sm:text-base leading-snug px-4 py-2 -ml-px border-l-2 transition-colors ${
                  isActive
                    ? "border-forest text-forest font-medium"
                    : "border-transparent text-ink/70 hover:text-ink hover:border-line"
                }`}
              >
                {s.label}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function Stat({ label, value, variant }) {
  const valueCls =
    variant === "green" ? "text-leaf" :
    variant === "yellow" ? "text-amber" :
    variant === "red" ? "text-clay" :
    "text-ink";
  return (
    <div className="bg-white rounded-xl border border-line py-1.5">
      <div className={`text-lg font-serif ${valueCls}`}>{value ?? 0}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted_text">{label}</div>
    </div>
  );
}

function ClassTile({ classification, count }) {
  const barCls = {
    GREEN: "bg-leaf",
    YELLOW: "bg-amber",
    RED: "bg-clay",
  }[classification];
  return (
    <div
      className="bg-white rounded-xl border border-line py-2 px-3 flex flex-col"
      data-testid={`brand-class-tile-${classification}`}
    >
      <div className={`w-10 h-1.5 ${barCls} rounded-full mb-1.5`} />
      <div className="relative">
        <h3 className="font-serif text-lg">{classification}</h3>
        <span className="absolute top-0 left-1/2 -translate-x-1/2 font-serif text-lg">
          {count ?? 0}
        </span>
      </div>
    </div>
  );
}

function OurTakeImageCarousel({ images, approvedItems }) {
  const [idx, setIdx] = useState(0);
  const n = images.length;
  const img = images[idx];
  const linkedItem = img?.clothing_item_id
    ? approvedItems.find((it) => it.id === img.clothing_item_id)
    : null;

  function prev() { setIdx((i) => (i - 1 + n) % n); }
  function next() { setIdx((i) => (i + 1) % n); }

  const inner = (
    <figure className="relative group" data-testid={`brand-our-take-image-${idx}`}>
      <img
        src={img.url}
        alt="Editor's image of the brand"
        loading="lazy"
        className="w-52 aspect-[3/4] rounded-2xl border border-line bg-champagne/30 object-cover"
      />
      {linkedItem?.slug && (
        <span
          className="absolute top-1.5 right-1.5 bg-ink/70 text-white rounded-full p-1 group-hover:bg-ink transition-colors"
          aria-label="Open clothing item"
          title="Open clothing item"
        >
          <ExternalLink className="w-3 h-3" />
        </span>
      )}
      <figcaption className="text-[11px] uppercase tracking-wider text-muted_text mt-1 text-center">
        Editor&apos;s image{n > 1 ? ` · ${idx + 1} / ${n}` : ""}
      </figcaption>
    </figure>
  );

  const slide = linkedItem?.slug ? (
    <Link to={`/clothing/${linkedItem.slug}`} title={linkedItem.name} className="block">
      {inner}
    </Link>
  ) : (
    <div>{inner}</div>
  );

  if (n <= 1) {
    return <div className="w-52 shrink-0">{slide}</div>;
  }

  return (
    <div className="w-52 shrink-0 relative" data-testid="brand-our-take-carousel">
      {slide}
      <button
        type="button"
        onClick={prev}
        aria-label="Previous image"
        className="absolute top-1/2 -translate-y-1/2 left-1 bg-ivory/95 hover:bg-ivory border border-line rounded-full p-1 shadow-sm"
        data-testid="brand-carousel-prev"
      >
        <ChevronLeftIcon className="w-4 h-4 text-ink" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next image"
        className="absolute top-1/2 -translate-y-1/2 right-1 bg-ivory/95 hover:bg-ivory border border-line rounded-full p-1 shadow-sm"
        data-testid="brand-carousel-next"
      >
        <ChevronRightIcon className="w-4 h-4 text-ink" />
      </button>
    </div>
  );
}
